/**
 * Body-worn cameras chop a single continuous recording into fixed-length files. Dropping all of
 * them at once should produce one job, not one per segment, so the transcript and the report cover
 * the whole incident.
 *
 * Two naming conventions are recognised:
 *  - HALOS: `part1-part2_part3-part4_part5` (mixed `-`/`_`), where part4 is the segment's start
 *    timestamp. Segments of one recording agree on parts 1, 2, 3 and 5 and differ only on part4.
 *  - Generic: a literal positive `partN` prefix, the rest of the name identifying the recording.
 *
 * Anything unrecognised, or a pattern matched by a single file, uploads as its own job.
 */

/**
 * part1-part2_part3-part4_part5. Parts 1-3 are single separator-free components: letting them
 * match separators too would make any name with five-plus components and an interior number look
 * like a segment set, fusing unrelated recordings. Only part5 keeps further separators
 * (`1025h85-1`, `1025h85 1`).
 */
const HALOS_NAME = /^([^-_]+)[-_]([^-_]+)[-_]([^-_]+)[-_](\d+)[-_](.+)$/;

/**
 * A literal positive `partN` prefix followed by a separator or nothing at all.
 */
const PART_PREFIX = /^part([1-9]\d*)(?:[-_ ](.*))?$/i;

export type RecordingGroup = {
  /** Segments in playback order. A single entry is an ordinary upload. */
  files: File[];
  /** Title for the merged recording; undefined for a lone file, which keeps the server-derived one. */
  title?: string;
};

type Segment = {
  file: File;
  key: string;
  /** Position within the recording, as normalised digits — see `compareOrder`. */
  order: string;
  /** Whether macOS added a trailing copy suffix such as ` (1)`. */
  isCopy: boolean;
  /** Title of the merged recording, given the final segment count. */
  title: (count: number) => string;
};

function baseName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

function normaliseDigits(digits: string): string {
  return digits.replace(/^0+(?=\d)/, '');
}

/**
 * Orders digit strings by value without going through `Number`, which rounds anything past 2^53
 * and would collapse two distinct positions onto one sort key.
 */
function compareOrder(a: string, b: string): number {
  return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
}

function parseSegment(file: File): Segment | null {
  const name = baseName(file.name);

  // The explicit `partN` prefix is the stronger signal, so it wins: a name like
  // `part1-BWC-Axon-2026-north` also satisfies the HALOS shape, and reading it as HALOS would put
  // the segment number in part1 — where segments never agree — so the set would never group.
  const prefixed = PART_PREFIX.exec(name);
  if (prefixed) {
    const [, index, rest = ''] = prefixed;
    const copySuffix = /(?:^| )\([1-9]\d*\)$/.exec(rest);
    const recordingName = copySuffix ? rest.slice(0, copySuffix.index) : rest;
    return {
      file,
      key: `part:${recordingName.toLowerCase()}`,
      order: index,
      isCopy: !!copySuffix,
      title: () => recordingName || name.slice(0, 4 + index.length),
    };
  }

  const halos = HALOS_NAME.exec(name);
  if (halos) {
    const [, p1, p2, p3, p4, p5] = halos;
    return {
      file,
      // Parts are joined through JSON rather than a delimiter: part5 may contain any character,
      // so an in-band separator would let two different recordings flatten onto one key.
      key: `halos:${JSON.stringify([p1, p2, p3, p5])}`,
      order: normaliseDigits(p4),
      isCopy: false,
      title: count => `${p1}-${p2}-${p3}-${count}partmerge-${p5}`,
    };
  }

  return null;
}

function orderRecording(bucket: Segment[]): Segment[] | null {
  if (bucket.length < 2) return null;

  const ordered = [...bucket].sort((a, b) => compareOrder(a.order, b.order));
  const selected: Segment[] = [];

  for (let i = 0; i < ordered.length;) {
    let end = i + 1;
    while (end < ordered.length && ordered[end].order === ordered[i].order) end += 1;

    const position = ordered.slice(i, end);
    const originals = position.filter(segment => !segment.isCopy);
    // Prefer the explicit filename over its macOS-numbered copies. Otherwise the position is ambiguous,
    // and stitching it would splice arbitrary footage into the evidence.
    if (position.length > 1 && originals.length !== 1) return null;

    selected.push(originals[0] ?? position[0]);
    i = end;
  }

  return selected.length > 1 ? selected : null;
}

/**
 * Buckets files that are segments of the same recording, preserving the order the files were
 * dropped in for everything else.
 */
export function groupRecordingSegments(files: File[]): RecordingGroup[] {
  const segments = files.map(parseSegment);

  const buckets = new Map<string, Segment[]>();
  for (const segment of segments) {
    if (!segment) continue;
    const bucket = buckets.get(segment.key);
    if (bucket) bucket.push(segment);
    else buckets.set(segment.key, [segment]);
  }

  const recordings = new Map<Segment, Segment[]>();
  for (const bucket of buckets.values()) {
    const ordered = orderRecording(bucket);
    if (!ordered) continue;
    ordered.forEach(segment => recordings.set(segment, ordered));
  }

  const groups: RecordingGroup[] = [];
  const emitted = new Set<Segment[]>();

  // Each recording takes the slot where its first segment was dropped; everything else, including
  // a file whose siblings turned out not to form a recording, keeps its own slot.
  files.forEach((file, index) => {
    const segment = segments[index];
    const ordered = segment && recordings.get(segment);
    if (!ordered || !segment) {
      groups.push({ files: [file] });
      return;
    }
    if (emitted.has(ordered)) return;

    emitted.add(ordered);
    groups.push({ files: ordered.map(s => s.file), title: ordered[0].title(ordered.length) });
  });

  return groups;
}
